import MapLink from './MapLink';

// The delivery person's route for one job: where to PICK UP, where to DROP, and the driving
// distance between them. Direction depends on the leg (see
// Docs/requirements/jewelry_rental/delivery/delivery_ticket_pickup_drop_distance.md):
//   OUTBOUND - pick up from the rental owner, drop to the customer.
//   RETURN   - pick up from the customer, drop back to the rental owner.
//
// Reused everywhere the route matters: DeliveryDashboard "My schedule" cards, the "My
// Deliveries" list, OrderDetailsModal, and AssignDeliveryModal (owner/admin route preview).
//
// Each address gets a "📍 map" control (shared MapLink - on-demand embed + keyless "Open in
// Google Maps" deep link, no Maps load until clicked). The turn-by-turn "Directions" link is
// a plain keyless google.com/maps/dir deep link. Degrades gracefully: with no distance the
// line reads "Distance unavailable"; the customer's own view omits `pickup` and the component
// just skips whatever isn't there.

const MAPS_STATUS_MESSAGE = {
  NO_API_KEY: 'Distance unavailable',
  LOOKUP_FAILED: 'Distance unavailable',
  INSUFFICIENT_ADDRESS: 'Distance unavailable — incomplete address',
};

export function formatTicketAddress(a) {
  if (!a) return null;
  const structured = [a.line1, a.line2, a.area, a.city, a.state, a.pincode].filter(Boolean).join(', ');
  // Prefer Google's own formatted string when the address was picked from search / detected.
  return a.formatted || structured || null;
}

// The end of a trip for a deep link: exact coordinates when pinned, else the address text.
function tripEnd(a) {
  if (a && a.lat != null && a.lat !== '' && a.lng != null && a.lng !== '') return `${a.lat},${a.lng}`;
  return formatTicketAddress(a);
}

export function ticketDistanceText(t) {
  if (!t) return null;
  if (t.mapsStatus === 'OK') {
    const dist = t.distanceText
      || (t.distanceMeters != null ? `${(t.distanceMeters / 1000).toFixed(1)} km` : null);
    if (!dist) return null;
    return t.durationText ? `${dist} · ~${t.durationText} driving` : dist;
  }
  return MAPS_STATUS_MESSAGE[t.mapsStatus] || 'Distance unavailable';
}

export function directionsUrl(t) {
  const origin = tripEnd(t?.pickup);
  const destination = tripEnd(t?.drop);
  if (!origin || !destination) return null;
  return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}`;
}

export default function DeliveryTicketPanel({ ticket, className = '' }) {
  if (!ticket) return null;
  const pickup = formatTicketAddress(ticket.pickup);
  const drop = formatTicketAddress(ticket.drop);
  const distance = ticketDistanceText(ticket);
  const url = directionsUrl(ticket);
  if (!pickup && !drop && !distance) return null;

  const isReturn = ticket.leg === 'RETURN';
  const legLabel = isReturn ? 'Return collection route' : 'Delivery route';
  const pickupWho = isReturn ? 'from customer' : 'from rental owner';
  const dropWho = isReturn ? 'to rental owner' : 'to customer';

  return (
    <div className={className}>
      <div className="text-uppercase text-muted small fw-semibold">{legLabel}</div>
      {pickup && (
        <div className="small">
          <span className="text-muted">Pick up ({pickupWho}):</span> {pickup}
          {ticket.pickup?.source === 'listing_location' && (
            <span className="text-muted"> (listing location)</span>
          )}{' '}
          <MapLink address={ticket.pickup} fallbackText={pickup} title="Pickup location" label="📍 map" />
        </div>
      )}
      {drop && (
        <div className="small">
          <span className="text-muted">Drop off ({dropWho}):</span> {drop}{' '}
          <MapLink address={ticket.drop} fallbackText={drop} title="Drop-off location" label="📍 map" />
        </div>
      )}
      {distance && (
        <div className="small">
          <span className="text-muted">Distance:</span> {distance}
        </div>
      )}
      {url && (
        <a className="small" href={url} target="_blank" rel="noreferrer">Directions ↗</a>
      )}
    </div>
  );
}
